<template>
  <InnoAddToCart
    :model-value="enteredQuantity"
    :name="product.id"
    :product="product"
    :count-in-cart="countInCart"
    :min-quantity="minQty"
    :max-quantity="maxQty"
    :pack-size="product.packSize"
    :is-active="product.availabilityData?.isActive"
    :is-available="product.availabilityData?.isAvailable"
    :is-buyable="product.availabilityData?.isBuyable"
    :is-in-stock="product.availabilityData?.isInStock"
    :available-quantity="product.availabilityData?.availableQuantity"
    :message="errorMessage || notAvailableMessage"
    :disabled="$cfg.product_quantity_control === 'stepper' ? disabledStepper : disabled"
    :loading="$cfg.product_quantity_control === 'stepper' ? loadingStepper : loading"
    :show-empty-details="reservedSpace"
    validate-on-mount
    :timeout="DEFAULT_DEBOUNCE_IN_MS"
    :allow-zero="$cfg.product_quantity_control === 'stepper'"
    @update:model-value="onInput"
    @update:cart-item-quantity="onChange"
    @update:validation="onValidationUpdate"
  >
    <slot />
  </InnoAddToCart>
</template>

<script setup lang="ts">
import { isDefined } from "@vueuse/core";
import { clone } from "lodash";
import { computed, ref, toRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useErrorsTranslator, useHistoricalEvents } from "@/core/composables";
import { useAnalyticsUtils } from "@/core/composables/useAnalyticsUtils";
import { useThemeContext } from "@/core/composables/useThemeContext";
import { LINE_ITEM_ID_URL_SEARCH_PARAM, LINE_ITEM_QUANTITY_LIMIT } from "@/core/constants";
import { ValidationErrorObjectType } from "@/core/enums";
import { getUrlSearchParam, Logger } from "@/core/utilities";
import { useShortCart } from "@/shared/cart/composables";
import { useConfigurableProduct } from "@/shared/catalog/composables";
import { useNotifications } from "@/shared/notification";
import { AddToCartModeType } from "@/ui-kit/enums";
import { DEFAULT_DEBOUNCE_IN_MS } from "../constants";
import type { Product, ShortLineItemFragment, VariationType, ValidationErrorType } from "@/core/api/graphql/types";
import QuantityControl from "@/shared/common/components/quantity-control.vue";

const emit = defineEmits<IEmits>();

const props = defineProps<IProps>();

const notifications = useNotifications();

interface IEmits {
  (event: "update:lineItem", lineItem: ShortLineItemFragment): void;
}

interface IProps {
  product: Product | VariationType;
  reservedSpace?: boolean;
}

const product = toRef(props, "product");
const { cart, addToCart, changeItemQuantityBatched, addToCartLoading, changeItemQuantityBatchedOverflowed } =
  useShortCart();
const { t } = useI18n();
const { translate } = useErrorsTranslator<ValidationErrorType>("validation_error");
const configurableLineItemId = getUrlSearchParam(LINE_ITEM_ID_URL_SEARCH_PARAM);
const {
  selectedConfigurationInput,
  changeCartConfiguredItemBatched,
  validateSections: validateConfigurableInput,
} = useConfigurableProduct(product.value.id);
const { trackAddItemToCart } = useAnalyticsUtils();
const { pushHistoricalEvent } = useHistoricalEvents();
const { themeContext } = useThemeContext();

const loading = ref(false);
const errorMessage = ref<string | undefined>();

const notAvailableMessage = computed(() => {
  if (!product.value.availabilityData?.isBuyable || !product.value.availabilityData?.isAvailable) {
    return t("validation_error.CART_PRODUCT_UNAVAILABLE");
  }
  return undefined;
});

const defaultMinQuantity = computed(() => (themeContext.value.settings.product_quantity_control === "button" ? 1 : 0));
const isConfigurable = computed(() => "isConfigurable" in product.value && product.value.isConfigurable);
const disabledStepper = computed(
  () =>
    !product.value.availabilityData?.isAvailable || changeItemQuantityBatchedOverflowed.value || addToCartLoading.value,
);
const loadingStepper = computed(() => changeItemQuantityBatchedOverflowed.value || addToCartLoading.value);
const countInCart = computed(() => getLineItem(cart.value?.items)?.quantity || 0);
const minQty = computed(() => product.value.minQuantity || defaultMinQuantity.value);
// Innovadis custom disabled and maxQty
const disabled = computed<boolean>(() => loading.value);
const maxQuantity = computed(() => props.product.maxQuantity);

const maxQty = computed<number>(() => {
  const allowBackOrder = props.product.availabilityData?.inventories?.some((item) => item.allowBackorder);
  if (allowBackOrder) {
    return Math.min(LINE_ITEM_QUANTITY_LIMIT);
  } else {
    return Math.min(
      props.product.availabilityData?.availableQuantity || LINE_ITEM_QUANTITY_LIMIT,
      maxQuantity.value || LINE_ITEM_QUANTITY_LIMIT,
    );
  }
});

const defaultQuantity = computed(() =>
  themeContext.value.settings.product_quantity_control === "button"
    ? countInCart.value || minQty.value
    : countInCart.value,
);
const enteredQuantity = ref(!disabled.value ? defaultQuantity.value : undefined);

function onInput(value: number): void {
  if (!Number.isFinite(value)) {
    enteredQuantity.value = undefined;
  } else if (value > LINE_ITEM_QUANTITY_LIMIT) {
    enteredQuantity.value = LINE_ITEM_QUANTITY_LIMIT;
  } else {
    enteredQuantity.value = value;
  }
}

/**
 * Process button click to add/update cart line item.
 */
async function onChange() {
  const lineItem = getLineItem(cart.value?.items);
  const mode = lineItem ? AddToCartModeType.Update : AddToCartModeType.Add;

  if (isConfigurable.value && !validateConfigurableInput()) {
    displayErrorMessage(mode, t("shared.catalog.product_details.product_configuration.check_your_configuration"));
    return;
  }

  loading.value = true;

  try {
    const updatedCart = await updateOrAddToCart(lineItem, mode);

    if ((isConfigurable.value && mode === AddToCartModeType.Add) || enteredQuantity.value === 0) {
      loading.value = false;
      return;
    }

    handleUpdateResult(updatedCart, mode);
  } finally {
    loading.value = false;
  }
}

async function updateOrAddToCart(lineItem: ShortLineItemFragment | undefined, mode: AddToCartModeType) {
  if (mode === AddToCartModeType.Update && enteredQuantity.value === undefined) {
    return cart.value;
  }
  if (mode === AddToCartModeType.Update && !!lineItem && enteredQuantity.value !== undefined) {
    return isConfigurable.value
      ? await changeCartConfiguredItemBatched(lineItem.id, enteredQuantity.value, selectedConfigurationInput.value)
      : await changeItemQuantityBatched(lineItem.id, enteredQuantity.value);
  }

  const quantity = enteredQuantity.value || minQty.value;
  const config = isConfigurable.value ? selectedConfigurationInput.value : undefined;
  const updatedCart = await addToCart(product.value.id, quantity, config);

  trackAddItemToCart(product.value, quantity);
  void pushHistoricalEvent({ eventType: "addToCart", productId: product.value.id });

  return updatedCart;
}

function handleUpdateResult(_cart: Awaited<ReturnType<typeof updateOrAddToCart>>, mode: AddToCartModeType) {
  if (!_cart) {
    return;
  }

  const lineItem = getLineItem(_cart.items);

  if (!lineItem) {
    Logger.error(onChange.name, 'The variable "lineItem" must be defined');
    displayErrorMessage(mode, getValidationErrors());
    return;
  }

  emit("update:lineItem", clone(lineItem));
}

function displayErrorMessage(mode: AddToCartModeType, message: string) {
  notifications.error({
    text: t(
      mode === AddToCartModeType.Update
        ? "common.messages.fail_to_change_quantity_in_cart"
        : "common.messages.fail_add_product_to_cart",
      { reason: message },
    ),
    duration: 4000,
    single: true,
  });
}

function getValidationErrors(): string {
  return (
    cart.value?.validationErrors
      ?.filter(
        (error) => error.objectId === product.value.id && error.objectType === ValidationErrorObjectType.CatalogProduct,
      )
      .map(translate)
      .join(" ") || ""
  );
}

function getLineItem(items?: ShortLineItemFragment[]): ShortLineItemFragment | undefined {
  if (isConfigurable.value) {
    return configurableLineItemId ? items?.find((item) => item.id === configurableLineItemId) : undefined;
  } else {
    return items?.find((item) => item.productId === product.value.id);
  }
}

function onValidationUpdate(validation: { isValid: true } | { isValid: false; errorMessage: string }) {
  if (product.value.availabilityData?.isBuyable && !validation.isValid) {
    errorMessage.value = validation.errorMessage;
  } else {
    errorMessage.value = undefined;
  }
}

watch(
  () => product.value.id,
  () => {
    enteredQuantity.value = !disabled.value ? defaultQuantity.value : undefined;
  },
);
</script>
